import React, { Component } from 'react';
import Backbone from 'backbone';

import store from '../../store/createStore';
import { Provider } from 'react-redux';

import SidebarComponent from './SidebarComponent';
import dx from '../../dx';
import { withRouter, matchPath } from 'react-router-dom';
import { t } from '../../locale';

// Backbone's own object doubles as a global event bus at runtime (AuthModel
// triggers 'contextSwitched' on it), but @types/backbone doesn't declare
// Events on the namespace.
const backboneEvents = (Backbone as unknown) as Backbone.Events;

const partnerId = dx.utils.getPartnerId();
const renterId = dx.utils.getRenterId();
const modules = [
    {
        type: 'module',
        url: `/partners/${partnerId}/renters/${renterId}/dashboard`,
        label: t('Dashbord'),
        icon: 'dashboard',
        needs: { module: 'shows', role: ['leietakere'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/renters/${renterId}/shows`,
        label: t('Arrangementer'),
        icon: 'calendar',
        needs: { module: 'shows', role: ['leietakere'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/dashboard`,
        label: t('Dashbord'),
        icon: 'dashboard',
        needs: { module: 'dashboard', role: ['kulturhussjef', 'culture_full_read_only'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/dashboard-legacy`,
        label: t('Dashbord, gammelt'),
        icon: 'dashboard',
        needs: { module: 'filmscheduler', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/shows`,
        label: t('Arrangementer'),
        icon: 'calendar',
        needs: { module: 'shows', role: ['kulturhussjef', 'culture_full_read_only', 'culture_events_read_only'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/culture`,
        label: t('Kalender'),
        icon: 'calendar',
        needs: { module: 'culture', role: ['kulturhussjef', 'culture_full_read_only', 'culture_events_read_only'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/campaigns`,
        label: t('Markedsføring'),
        icon: 'bullhorn',
        needs: { module: 'culture', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/products`,
        label: t('Produkter'),
        icon: 'shopping-cart',
        needs: { module: 'dxpos', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/booking-calendar`,
        label: t('Bookingkalender'),
        icon: 'calendar-plus-o',
        needs: { module: 'filmscheduler', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/filmscheduler`,
        label: t('Filmprogrammering'),
        icon: 'film',
        needs: { module: 'filmscheduler', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/renteradmin`,
        label: t('Leietakere'),
        icon: 'sitemap',
        needs: { module: 'renters', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/orders`,
        label: t('Prosjekter'),
        icon: 'shopping-basket',
        needs: { module: 'renters', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/mediabase`,
        label: t('Mediabase'),
        icon: 'film',
        needs: { module: 'productions', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/crm`,
        label: t('Kunder'),
        icon: 'users',
        needs: { module: 'crm', role: ['kulturhussjef'] },
    },
    {
        type: 'module',
        url: `/partners/${partnerId}/partneradmin`,
        label: t('Partneradmin'),
        icon: 'building',
        needs: { module: 'partneradmin', role: ['kulturhussjef'] },
    },
];

const getSidebarItems = (sudoPrefix, activePath) => {
    const authUser = dx.utils.getAuthUser();
    let sidebaritems = [];
    if (authUser.partners.length > 1) {
        sidebaritems = sidebaritems.concat([
            { type: 'module', url: '/home', label: t('Hjem'), icon: 'home' },
            { type: 'spacer' },
        ]);
    }

    sidebaritems = sidebaritems.concat(
        modules.filter(m => dx.utils.hasModule(m.needs.module) && dx.utils.hasRole(m.needs.role))
    );
    if (dx.utils.hasRole(['kulturhussjef'])) {
        sidebaritems.push({
            type: 'module',
            url: `/partners/${partnerId}/report`,
            label: t('Rapporter'),
            icon: 'file-text',
        });
    }
    if (partnerId > 1 && dx.utils.hasRole(['kulturhussjef'])) {
        sidebaritems.push({
            type: 'module',
            url: `/partners/${partnerId}/partnerprofile`,
            label: t('Innstillinger'),
            icon: 'cog',
        });
    }

    // Add React 19 module only for administrators
    // Keeping this name of module for now, we can change it to booking when it's actullly revritten to booking module
    if (dx.utils.hasModule('react19-module')) {
        sidebaritems.push({
            type: 'module',
            url: `/partners/${partnerId}/react19-module`,
            label: 'React 19 Demo',
            icon: 'code',
        });
    }
    // TODO: This looks like same logic as for dx.utils.isSuperUser(), check if we can use it here instead
    if (authUser.partners.find(p => p.partnerID === 1)) {
        sidebaritems = sidebaritems.concat([
            { type: 'spacer' },
            { type: 'module', url: '/partners/1/partneradmin', label: t('Partneradmin'), icon: 'building' },
            { type: 'module', url: '/partners/1/reportsadmin', label: t('Rapportadmin (beta)'), icon: 'line-chart' },
            { type: 'module', url: '/partners/1/useradmin', label: t('Useradmin'), icon: 'users' },
            { type: 'module', url: '/partners/1/mediabase', label: t('Mediabase (som admin)'), icon: 'film' },
            {
                type: 'module',
                url: (process.env.REACT_APP_BACKEND_URL || '') + '/apiweb/health',
                label: t('Health status'),
                icon: 'medkit',
                aHrefLink: true,
            },
        ]);
    }
    return sidebaritems.map(sbi =>
        sbi.url
            ? {
                  ...sbi,
                  url: dx.utils.getPartnerIdFromUrl(sbi.url) > 1 ? sudoPrefix + sbi.url : sbi.url,
                  active: !!matchPath(activePath, { path: sbi.url }),
              }
            : sbi
    );
};

// @ts-ignore
@withRouter
class Sidebar extends Component<any, any> {
    [key: string]: any;
    static [key: string]: any;

    // getSidebarItems() reads the auth model through dx.utils.hasModule /
    // hasRole rather than from props or state, so React has no way of knowing
    // when it changes. AuthModel re-broadcasts every change as 'contextSwitched'
    // (fired e.g. when Partneradmin toggles a module and refetches auth), and
    // the menu has to be rebuilt for that to show without a page reload.
    componentDidMount() {
        backboneEvents.on('contextSwitched', this.onContextSwitched);
    }

    componentWillUnmount() {
        backboneEvents.off('contextSwitched', this.onContextSwitched);
    }

    onContextSwitched = () => {
        this.forceUpdate();
    };

    render() {
        return (
            <Provider store={store}>
                <SidebarComponent
                    partnerId={this.props.partnerId}
                    renterId={this.props.renterId}
                    items={
                        this.props.waitingForAuth
                            ? []
                            : getSidebarItems(this.props.sudoPrefix, this.props.location.pathname)
                    }
                    waitingForAuth={this.props.waitingForAuth}
                />
            </Provider>
        );
    }
}

export default Sidebar;
